/**
 * The line-item editor. One row per requested item; the row's shape follows its kind.
 *
 * Items are picked from the LIVE catalog rather than typed, which is what makes the
 * engine's exact-match rule structurally satisfiable: a dropdown can only emit a real
 * `sku`, so the "'pro' silently priced as Pro Plus" class of bug cannot be reintroduced
 * through the UI. Free-text entry stays available in the engine for pasted payloads.
 */
import { Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { formatMoney } from '../pricing/cents';
import type {
  LineKind,
  PricedLine,
  QuoteCatalog,
  QuoteRequestLine,
  ShippingRegion,
} from '../pricing/types';

const KIND_LABELS: { value: LineKind; label: string }[] = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'addon', label: 'Add-on' },
  { value: 'device', label: 'Hardware' },
  { value: 'shipping', label: 'Shipping' },
];

const REGION_LABELS: { value: ShippingRegion; label: string }[] = [
  { value: 'internal', label: 'US (domestic)' },
  { value: 'international', label: 'International' },
];

/**
 * Keep a discount field's raw text usable while it is being typed: digits, at most one
 * decimal point, at most two decimals, capped at 100.
 *
 * Returns a STRING so partial input survives a re-render. Parsing to a number on every
 * keystroke is what once made a decimal impossible to type — "12." became 12 and the dot
 * vanished under the caret — and the same trap applies to "7.5%".
 */
export function sanitizePercent(input: string): string {
  let s = input.replace(/[^0-9.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) {
    const whole = s.slice(0, dot);
    const frac = s.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    s = `${whole}.${frac}`;
  }
  // A discount over 100% is not a typo we should carry into the engine's flag list; clamp
  // the obvious case here and let the engine reject anything else.
  if (s && Number(s) > 100) s = '100';
  return s;
}

export interface QuoteLineRowsProps {
  lines: QuoteRequestLine[];
  catalog: QuoteCatalog;
  currency: string;
  /** Priced results; each row finds its own by `rowId`. */
  priced: PricedLine[];
  onChange: (id: string, patch: Partial<QuoteRequestLine>) => void;
  onRemove: (id: string) => void;
}

export function QuoteLineRows({
  lines,
  catalog,
  currency,
  priced,
  onChange,
  onRemove,
}: QuoteLineRowsProps) {
  /**
   * Pair a row with its priced result by row id — never by sku. The plan dropdown emits
   * an alias while the engine records the plan `_id`, so a sku comparison silently missed
   * every subscription, and two rows of the same product both matched the first result.
   *
   */
  const pricedFor = (line: QuoteRequestLine): PricedLine | undefined =>
    priced.find((p) => p.rowId === line.id);

  /**
   * Options come from live Mongo docs, so a blank identifier is possible. Radix THROWS on
   * a SelectItem whose value is the empty string (it reserves that for "no selection"), and
   * an unrenderable option would take the whole page down — so drop them. A dropped option
   * is also unusable by the engine, which matches on exactly these identifiers.
   */
  /** The catalog add-on a row points at, for its tiers. */
  const addOnFor = (sku?: string) =>
    sku ? catalog.addOns.find((a) => a.key === sku) : undefined;

  const optionsFor = (kind: LineKind) => {
    const raw =
      kind === 'subscription'
        ? catalog.plans.map((p) => ({ value: p.alias || p._id, label: p.name || p._id }))
        : kind === 'addon'
          ? catalog.addOns.map((a) => ({ value: a.key, label: a.name || a.key }))
          : kind === 'shipping'
          ? catalog.shippings.map((s) => ({ value: s._id, label: s.name || s._id }))
          : catalog.products.map((p) => ({ value: p._id, label: p.name || p._id }));
    return raw.filter((o) => o.value);
  };

  if (!lines.length) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="quote-lines-empty">
        {'No line items yet — add a subscription, hardware, or shipping row below.'}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="quote-lines">
      <div className="hidden gap-2 px-1 text-[11px] font-medium text-muted-foreground md:grid md:grid-cols-[7rem_1fr_4.5rem_7rem_5rem_8rem_2.5rem]">
        <span>{'Type'}</span>
        <span>{'Item'}</span>
        <span>{'Qty'}</span>
        <span>{'Unit price'}</span>
        <span>{'Disc %'}</span>
        <span className="text-right">{'Amount'}</span>
        <span />
      </div>

      {lines.map((line) => {
        const result = pricedFor(line);
        const options = optionsFor(line.kind);
        const isShipping = line.kind === 'shipping';
        const isSubscription = line.kind === 'subscription' || line.kind === 'addon';

        return (
          <div
            key={line.id}
            className="grid grid-cols-1 items-start gap-2 rounded-md border p-2 md:grid-cols-[7rem_1fr_4.5rem_7rem_5rem_8rem_2.5rem] md:border-0 md:p-1"
            data-testid={`quote-line-${line.id}`}
          >
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] md:hidden">{'Type'}</Label>
              <Select
                value={line.kind}
                onValueChange={(v) =>
                  // Changing kind invalidates the selected item — a plan alias is
                  // meaningless in the hardware catalog.
                  onChange(line.id, {
                    kind: v as LineKind,
                    sku: undefined,
                    query: undefined,
                    // A discount is meaningless on hardware and shipping, so switching
                    // kind clears it rather than leaving an inapplicable value the engine
                    // would then have to flag.
                    discountPct: null,
                    term: v === 'subscription' || v === 'addon' ? 'yearly' : undefined,
                  })
                }
              >
                <SelectTrigger
                  className="cursor-pointer"
                  aria-label="Line type"
                  data-testid={`quote-line-${line.id}-kind`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_LABELS.map((k) => (
                    <SelectItem key={k.value} value={k.value} className="cursor-pointer">
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[11px] md:hidden">{'Item'}</Label>
              <Select
                value={line.sku ?? ''}
                onValueChange={(v) => onChange(line.id, { sku: v, query: undefined })}
              >
                <SelectTrigger
                  className="cursor-pointer"
                  aria-label="Catalog item"
                  data-testid={`quote-line-${line.id}-sku`}
                >
                  <SelectValue placeholder={options.length ? 'Select…' : 'Catalog unavailable'} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="cursor-pointer">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isShipping ? (
                <Select
                  value={line.region ?? ''}
                  onValueChange={(v) => onChange(line.id, { region: v as ShippingRegion })}
                >
                  <SelectTrigger
                    className="h-8 cursor-pointer text-xs"
                    aria-label="Shipping region"
                    data-testid={`quote-line-${line.id}-region`}
                  >
                    <SelectValue placeholder="Region — from customer country" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGION_LABELS.map((r) => (
                      <SelectItem key={r.value} value={r.value} className="cursor-pointer">
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              {/* A tiered add-on is a different product per tier — OptiSound Standard is
                  $162 and Pro is $270 — so the tier has to be pickable, not implied. */}
              {line.kind === 'addon' && (addOnFor(line.sku)?.tiers?.length ?? 0) > 0 ? (
                <Select
                  value={line.tier ?? addOnFor(line.sku)?.defaultTier ?? ''}
                  onValueChange={(v) => onChange(line.id, { tier: v })}
                >
                  <SelectTrigger
                    className="h-8 cursor-pointer text-xs"
                    aria-label="Add-on tier"
                    data-testid={`quote-line-${line.id}-tier`}
                  >
                    <SelectValue placeholder="Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {(addOnFor(line.sku)?.tiers ?? []).map((t) => (
                      <SelectItem key={t.key} value={t.key} className="cursor-pointer">
                        {t.name ?? t.key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[11px] md:hidden">{'Qty'}</Label>
              <Input
                value={line.quantity ?? ''}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, '');
                  onChange(line.id, { quantity: digits === '' ? null : Number(digits) });
                }}
                placeholder="1"
                aria-label="Quantity"
                data-testid={`quote-line-${line.id}-qty`}
              />
            </div>

            {/* Read-only. The catalog is the only source of a rate — an operator who could
                type one could quote a price the business never agreed to, and nothing
                downstream would know the difference. Negotiation happens through the
                discount field beside it, which is visible on the printed quote. */}
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] md:hidden">{'Unit price'}</Label>
              <div
                className="flex items-center pt-1 text-sm text-muted-foreground md:pt-2"
                title={
                  isShipping
                    ? 'Shipping is priced per region as first-device + additional, so a single unit price does not apply.'
                    : 'From the live catalog. Use the discount column to quote below list.'
                }
                data-testid={`quote-line-${line.id}-unitPrice`}
              >
                {isShipping
                  ? 'tiered'
                  : result?.unitCents != null
                    ? formatMoney(result.unitCents, currency)
                    : '—'}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[11px] md:hidden">{'Disc %'}</Label>
              {isSubscription ? (
                <Input
                  value={line.discountPct ?? ''}
                  onChange={(e) =>
                    // Raw string, not a parsed number — see QuoteRequestLine.discountPct.
                    onChange(line.id, { discountPct: sanitizePercent(e.target.value) || null })
                  }
                  placeholder="0"
                  aria-label="Discount percent"
                  title="Percentage off this line. Each subscription and add-on can carry its own."
                  data-testid={`quote-line-${line.id}-discountPct`}
                />
              ) : (
                <div
                  className="flex items-center pt-1 text-xs text-muted-foreground md:pt-2"
                  title="Discounts apply to subscriptions and add-ons only, never hardware or shipping."
                  data-testid={`quote-line-${line.id}-discount-na`}
                >
                  {'—'}
                </div>
              )}
            </div>

            {/* Amount = what this line is actually charged. On a discounted line that is
                the NET, with the arithmetic shown beneath it — the figure a rep reads off
                and repeats to a customer should be the one being billed, not a list price
                they then have to do subtraction against. */}
            <div className="flex flex-col items-end pt-1 text-sm md:pt-2">
              <div data-testid={`quote-line-${line.id}-amount`}>
                {result?.amountCents != null ? (
                  <span className="font-medium">
                    {formatMoney(result.amountCents - (result.discountCents ?? 0), currency)}
                  </span>
                ) : (
                  <span className="text-xs text-destructive">{'unpriced'}</span>
                )}
              </div>
              {result?.discountCents ? (
                <div
                  className="text-[11px] text-muted-foreground"
                  data-testid={`quote-line-${line.id}-breakdown`}
                >
                  {`${formatMoney(result.amountCents ?? 0, currency)} − ${formatMoney(
                    result.discountCents,
                    currency,
                  )}`}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end md:pt-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onRemove(line.id)}
                className="cursor-pointer"
                aria-label="Remove line"
                data-testid={`quote-line-${line.id}-remove`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
